<template>
  <div class="dealer-conversational-registration">
    <!-- Chat Container -->
    <v-card class="chat-container" elevation="2">
      <v-card-title class="chat-header">
        Join AutoSyncX&trade; Dealer Network - Let's Chat!
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')" v-if="showCloseButton">
          <v-icon class="material-icons-round">close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="chat-body">
        <!-- Messages -->
        <div class="messages-container" ref="messagesContainer">
          <div
            v-for="(message, index) in messages"
            :key="index"
            :class="['message', message.role === 'user' ? 'user-message' : 'assistant-message']"
          >
            <div class="message-content">
              <div class="message-text" v-html="formatMessage(message.content)"></div>
              <div class="message-time">{{ formatTime(message.timestamp) }}</div>
            </div>
          </div>

          <!-- Typing indicator -->
          <div v-if="isTyping" class="message assistant-message">
            <div class="message-content">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Input Area -->
        <div class="input-area" v-if="!isComplete">
          <!-- Google Autocomplete for Business Address -->
          <div v-if="conversationState === 'business_address'" class="address-input-area mb-3">
            <vuetify-google-autocomplete
              id="business-address"
              v-model="businessAddress"
              label="Business Address"
              outlined
              dense
              class="address-autocomplete"
              @placechanged="handleAddressSelection"
              country="us"
              :loading="isTyping"
            >
              <template v-slot:prepend-inner>
                <v-icon class="material-icons-round" color="primary">location_on</v-icon>
              </template>
            </vuetify-google-autocomplete>
            <div class="address-actions mt-2">
              <v-btn
                @click="submitAddress"
                color="blue"
                small
                :disabled="!businessAddress || isTyping"
                :loading="isTyping"
              >
                <v-icon left small class="material-icons-round">send</v-icon>
                Submit Address
              </v-btn>
            </div>
          </div>

          <!-- File Upload for License -->
          <div v-if="conversationState === 'license_upload'" class="license-upload-area mb-3">
            <v-file-input
              v-model="licenseFile"
              label="Upload Dealer License (Optional)"
              accept="image/*,.pdf"
              outlined
              dense
              show-size
              @change="handleFileUpload"
              class="license-file-input"
            >
              <template v-slot:selection="{ text }">
                <v-chip small color="blue" class="ma-1">
                  <v-icon left small class="material-icons-round">description</v-icon>
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>
            <div class="upload-actions">
              <v-btn
                @click="skipLicenseUpload"
                text
                color="grey"
                small
                class="mr-2"
              >
                Skip for now
              </v-btn>
              <v-btn
                @click="uploadLicense"
                color="blue"
                small
                :disabled="!licenseFile"
                :loading="uploadingLicense"
              >
                <v-icon left small class="material-icons-round">cloud_upload</v-icon>
                Upload License
              </v-btn>
            </div>
          </div>

          <!-- Regular Text Input -->
          <v-text-field
            v-if="conversationState !== 'business_address' && conversationState !== 'license_upload'"
            v-model="currentMessage"
            placeholder="Type your message..."
            outlined
            dense
            hide-details
            @keyup.enter="sendMessage"
            :disabled="isTyping"
            class="message-input"
          >
            <template v-slot:append>
              <v-btn
                icon
                @click="sendMessage"
                :disabled="!currentMessage.trim() || isTyping"
                color="blue"
              >
                <v-icon class="material-icons-round">send</v-icon>
              </v-btn>
            </template>
          </v-text-field>
        </div>

        <!-- Registration Complete -->
        <div v-if="isComplete && !registrationCompleted" class="completion-area">
          <v-card class="completion-card" elevation="1">
            <v-card-title class="completion-title">
              <v-icon class="material-icons-round mr-2" color="success">check_circle</v-icon>
              Almost Done!
            </v-card-title>
            <v-card-text>
              <p>Great! I have collected all the information we need. Let me create your dealer account now.</p>
              <v-btn
                @click="completeRegistration"
                color="green"
                large
                block
                :loading="completingRegistration"
                class="mt-3"
              >
                <v-icon class="material-icons-round mr-2">rocket_launch</v-icon>
                Create My Dealer Account
              </v-btn>
            </v-card-text>
          </v-card>
        </div>

        <!-- Registration Success -->
        <div v-if="registrationCompleted" class="success-area">
          <v-card class="success-card" elevation="1">
            <v-card-title class="success-title">
              <v-icon class="material-icons-round mr-2" color="success">celebration</v-icon>
              Welcome to AutoSyncX&trade;!
            </v-card-title>
            <v-card-text>
              <p class="success-message">
                Your dealer account has been created successfully!
              </p>
              <div class="success-details">
                <p><strong>Dealership:</strong> {{ registeredDealer && registeredDealer.business_name }}</p>
                <p><strong>Contact:</strong> {{ registeredDealer && registeredDealer.first_name }} {{ registeredDealer && registeredDealer.last_name }}</p>
                <p><strong>Email:</strong> {{ registeredDealer && registeredDealer.email }}</p>
              </div>
              <v-alert type="info" class="mt-3">
                <strong>Next Steps:</strong><br>
                &bull; Check your email for login credentials<br>
                &bull; Our team will review your application<br>
                &bull; You will receive approval notification within 24-48 hours
              </v-alert>
              <v-btn
                @click="$emit('close')"
                color="blue"
                large
                block
                class="mt-4"
              >
                <v-icon class="material-icons-round mr-2">dashboard</v-icon>
                Continue to Portal
              </v-btn>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>

export default {
  name: 'DealerConversationalRegistration',
  props: {
    showCloseButton: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      sessionId: null,
      messages: [],
      currentMessage: '',
      isTyping: false,
      isComplete: false,
      registrationCompleted: false,
      completingRegistration: false,
      collectedData: {},
      conversationState: 'greeting',
      registeredDealer: null,
      licenseFile: null,
      uploadingLicense: false,
      licenseUploaded: false,
      businessAddress: '',
      selectedAddressData: null
    }
  },
  mounted() {
    this.initializeConversation()
  },
  methods: {
    async initializeConversation() {
      try {
        const response = await axios.post('/api/dealer-conversational-registration/init', {
          domain: window.location.hostname
        });

        if (response.data.flag === 1) {
          this.sessionId = response.data.session_id;

          // Add greeting message
          this.messages.push({
            role: 'assistant',
            content: response.data.greeting,
            timestamp: new Date()
          });

          // Add dealership name question immediately
          setTimeout(() => {
            this.messages.push({
              role: 'assistant',
              content: "What is the name of your dealership?",
              timestamp: new Date()
            });
            this.scrollToBottom();
          }, 1000);

          this.scrollToBottom();
        }
      } catch (error) {
        console.error('Error initializing dealer conversation:', error);
        this.messages.push({
          role: 'assistant',
          content: 'Sorry, there was an error starting our conversation. Please refresh and try again.',
          timestamp: new Date()
        });
      }
    },

    async sendMessage() {
      if (!this.currentMessage.trim() || this.isTyping) return;

      const userMessage = this.currentMessage.trim();
      this.currentMessage = '';

      // Add user message
      this.messages.push({
        role: 'user',
        content: userMessage,
        timestamp: new Date()
      });

      this.scrollToBottom();
      this.isTyping = true;

      try {
        const response = await axios.post('/api/dealer-conversational-registration/message', {
          session_id: this.sessionId,
          message: userMessage
        });

        if (response.data.flag === 1) {
          setTimeout(() => {
            this.isTyping = false;
            
            // Add AI response
            this.messages.push({
              role: 'assistant',
              content: response.data.message,
              timestamp: new Date()
            });

            // Update collected data and conversation state
            this.collectedData = response.data.collected_data || {};
            this.conversationState = response.data.conversation_state || 'greeting';
            this.isComplete = response.data.is_complete || false;

            this.scrollToBottom();
          }, 1500); // Simulate typing delay
        }
      } catch (error) {
        this.isTyping = false;
        console.error('Error sending message:', error);
        this.messages.push({
          role: 'assistant',
          content: 'Sorry, there was an error processing your message. Please try again.',
          timestamp: new Date()
        });
        this.scrollToBottom();
      }
    },

    async completeRegistration() {
      this.completingRegistration = true;

      try {
        // First validate the session
        const validateResponse = await axios.get(`/api/dealer-conversational-registration/validate/${this.sessionId}`);

        if (validateResponse.data.flag !== 1) {
          this.messages.push({
            role: 'assistant',
            content: 'Your session has expired. Please refresh the page and start the registration process again.',
            timestamp: new Date()
          });
          this.completingRegistration = false;
          return;
        }

        // Proceed with registration
        const response = await axios.post('/api/dealer-conversational-registration/complete', {
          session_id: this.sessionId
        });

        if (response.data.flag === 1) {
          this.registeredDealer = response.data.data;
          this.registrationCompleted = true;
          this.scrollToBottom();
        } else {
          this.messages.push({
            role: 'assistant',
            content: response.data.message || 'Sorry, there was an error creating your account. Please try again.',
            timestamp: new Date()
          });
        }
      } catch (error) {
        console.error('Error completing registration:', error);

        let errorMessage = 'Sorry, there was an error creating your account. Please try again.';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        this.messages.push({
          role: 'assistant',
          content: errorMessage,
          timestamp: new Date()
        });
      } finally {
        this.completingRegistration = false;
      }
    },

    formatMessage(content) {
      // Convert line breaks and format links
      return content
        .replace(/\n/g, '<br>')
        .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>');
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    handleFileUpload(file) {
      // File is automatically set to licenseFile by v-model
      console.log('File selected:', file);
    },

    async uploadLicense() {
      if (!this.licenseFile) return;

      this.uploadingLicense = true;

      try {
        const formData = new FormData();
        formData.append('license_file', this.licenseFile);
        formData.append('session_id', this.sessionId);

        // Upload file to server
        const response = await axios.post('/api/dealer-conversational-registration/upload-license', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.flag === 1) {
          this.licenseUploaded = true;

          // Add success message
          this.messages.push({
            role: 'assistant',
            content: 'Great! Your dealer license has been uploaded successfully. Now let me create your dealer account.',
            timestamp: new Date()
          });

          // Move to completion
          this.isComplete = true;
          this.scrollToBottom();
        }
      } catch (error) {
        console.error('Error uploading license:', error);
        this.messages.push({
          role: 'assistant',
          content: 'Sorry, there was an error uploading your license. You can skip this step for now and upload it later.',
          timestamp: new Date()
        });
      } finally {
        this.uploadingLicense = false;
      }
    },

    handleAddressSelection(addressData) {
      // Store the selected address data
      this.selectedAddressData = {
        formatted_address: addressData.name,
        street_number: addressData.street_number || '',
        route: addressData.route || '',
        locality: addressData.locality || '',
        administrative_area_level_1: addressData.administrative_area_level_1 || '',
        postal_code: addressData.postal_code || '',
        country: addressData.country || 'US'
      };

      console.log('Address selected:', this.selectedAddressData);
    },

    async submitAddress() {
      if (!this.businessAddress) return;

      this.isTyping = true;

      try {
        // Add user message
        this.messages.push({
          role: 'user',
          content: this.businessAddress,
          timestamp: new Date()
        });

        // Send address to backend
        const response = await axios.post('/api/dealer-conversational-registration/message', {
          session_id: this.sessionId,
          message: this.businessAddress,
          address_data: this.selectedAddressData // Send structured address data
        });

        if (response.data.flag === 1) {
          // Add assistant response
          this.messages.push({
            role: 'assistant',
            content: response.data.message,
            timestamp: new Date()
          });

          // Update conversation state
          this.conversationState = response.data.conversation_state;

          // Store collected data
          if (response.data.extracted_data) {
            this.collectedData = { ...this.collectedData, ...response.data.extracted_data };
          }

          // Check if complete
          if (response.data.is_complete) {
            this.isComplete = true;
          }

          // Clear address input
          this.businessAddress = '';
          this.selectedAddressData = null;
        }
      } catch (error) {
        console.error('Error submitting address:', error);
        this.messages.push({
          role: 'assistant',
          content: 'Sorry, there was an error processing your address. Please try again.',
          timestamp: new Date()
        });
      } finally {
        this.isTyping = false;
        this.scrollToBottom();
      }
    },

    skipLicenseUpload() {
      // Add message about skipping
      this.messages.push({
        role: 'user',
        content: 'Skip license upload for now',
        timestamp: new Date()
      });

      this.messages.push({
        role: 'assistant',
        content: 'No problem! You can upload your dealer license later. Let me create your dealer account now.',
        timestamp: new Date()
      });

      // Move to completion
      this.isComplete = true;
      this.scrollToBottom();
    }
  }
}
</script>

<style scoped>
.dealer-conversational-registration {
  max-width: 600px;
  margin: 0 auto;
  height: 100%;
}

.chat-container {
  min-height: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  border-radius: 16px !important;
  overflow: hidden;
}

.chat-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 1.1rem;
}

.chat-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  min-height: 400px;
  max-height: calc(80vh - 64px);
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa;
}

.message {
  margin-bottom: 16px;
  display: flex;
}

.user-message {
  justify-content: flex-end;
}

.assistant-message {
  justify-content: flex-start;
}

.message-content {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 18px;
  position: relative;
}

.user-message .message-content {
  background: #1976d2;
  color: white;
  border-bottom-right-radius: 4px;
}

.assistant-message .message-content {
  background: white;
  color: #333;
  border: 1px solid #e0e0e0;
  border-bottom-left-radius: 4px;
}

.message-text {
  line-height: 1.4;
}

.message-time {
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: 4px;
  text-align: right;
}

.assistant-message .message-time {
  text-align: left;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #666;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing {
  0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

.input-area {
  padding: 16px 20px;
  background: white;
  border-top: 1px solid #e0e0e0;
}

.license-upload-area {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border: 2px dashed #dee2e6;
}

.address-input-area {
  padding: 16px;
  background: #f0f8ff;
  border-radius: 8px;
  border: 2px solid #e3f2fd;
}

.address-autocomplete {
  margin-bottom: 8px;
}

.address-actions {
  display: flex;
  justify-content: flex-end;
}

.license-file-input {
  margin-bottom: 12px;
}

.upload-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.completion-area,
.success-area {
  padding: 20px;
  background: white;
}

.completion-card,
.success-card {
  border-radius: 12px !important;
}

.completion-title,
.success-title {
  font-size: 1.2rem;
  font-weight: 600;
  padding-bottom: 8px;
}

.success-message {
  font-size: 1.1rem;
  margin-bottom: 16px;
}

.success-details p {
  margin-bottom: 8px;
  font-size: 0.95rem;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .chat-container {
    height: 90vh;
    border-radius: 0 !important;
  }
  
  .message-content {
    max-width: 90%;
  }
  
  .chat-header {
    font-size: 1rem;
    padding: 12px 16px;
  }
}
</style>
